import { useCelebrationSlot } from "@/lib/celebrationQueue";
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame } from "lucide-react";
import { useGradeTier } from "@/hooks/useGradeTier";
import { useKidsDarkMode, KT } from "@/context/KidsDarkModeContext";
import { cn } from "@/lib/utils";

interface StreakCelebrationOverlayProps {
    userId?: string;
    streak: number;
    longestStreak: number;
    // True while UserProgressContext's real fetch is still in flight. Gating on
    // this — instead of a fixed timer — is the fix: `streak` starts at 0 (or a
    // stale localStorage cache) before the real value loads, and a fixed
    // 1500ms guess raced against variable network latency meant a real streak
    // arriving late could read as a "jump" from 0 and spuriously celebrate.
    isLoading: boolean;
    hasVerifiedProgress: boolean;
}

const ACK_STREAK_KEY = 'novari_acknowledged_streak';
const STREAK_MILESTONES = [3, 7, 14, 30, 50, 100, 200, 365];

/** The last seven days ending today, oldest first, with real weekday names. */
function lastSevenDays() {
    const today = new Date();
    return Array.from({ length: 7 }, (_, i) => {
        const d = new Date(today);
        d.setDate(today.getDate() - (6 - i));
        const name = d.toLocaleDateString(undefined, { weekday: "long" });
        return { key: d.toDateString(), name, initial: d.toLocaleDateString(undefined, { weekday: "narrow" }) };
    });
}

function nextMilestone(streak: number): number {
    return STREAK_MILESTONES.find((m) => m > streak) ?? streak;
}

/**
 * Celebrates a study streak the moment it grows. Acknowledges in localStorage
 * so a page refresh can't re-trigger it, but never reads/writes that
 * acknowledgement until the real streak has actually loaded (isLoading===false)
 * — so a transient default/cached value is never mistaken for a real change.
 * Only meaningful streaks (2+) celebrate.
 */
export const StreakCelebrationOverlay: React.FC<StreakCelebrationOverlayProps> = ({
    userId,
    streak,
    longestStreak,
    isLoading,
    hasVerifiedProgress,
}) => {
    const [show, setShow] = useState(false);
    const { visible, onExitComplete } = useCelebrationSlot(show, 2);
    const [announced, setAnnounced] = useState(streak);
    const hydrated = useRef(false);
    const lastConfirmed = useRef<number | null>(null);

    useEffect(() => {
        if (!userId || isLoading || !hasVerifiedProgress) return;
        const key = `${ACK_STREAK_KEY}:${userId}`;
        let ack = lastConfirmed.current ?? -1;
        try {
            const saved = Number(localStorage.getItem(key) ?? -1);
            if (Number.isSafeInteger(saved) && saved >= 0) ack = saved;
        } catch { /* Storage is optional; the in-memory baseline still prevents an initial celebration. */ }
        const celebrate = hydrated.current && ack >= 0 && streak > ack && streak >= 2;
        hydrated.current = true;
        lastConfirmed.current = streak;
        try { localStorage.setItem(key, String(streak)); } catch { /* Private browsing may block storage. */ }
        if (celebrate) {
            setAnnounced(streak);
            setShow(true);
        } else if (streak < announced) {
            setShow(false);
        }
    }, [userId, streak, isLoading, hasVerifiedProgress, announced]);

    useEffect(() => {
        if (!visible) return;
        const timer = setTimeout(() => setShow(false), 8000);
        return () => clearTimeout(timer);
    }, [visible, announced]);

    // Accessibility / Modals: dismiss with Escape while shown.
    useEffect(() => {
        if (!visible) return;
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setShow(false); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [visible]);


    // Move focus into the celebration and give it back afterwards, so a
    // keyboard or screen-reader user isn't left behind the overlay.
    const returnFocus = useRef<HTMLElement | null>(null);
    useEffect(() => {
        if (visible) {
            returnFocus.current = document.activeElement as HTMLElement | null;
        } else if (returnFocus.current) {
            returnFocus.current.focus?.();
            returnFocus.current = null;
        }
    }, [visible]);

    const { isKids } = useGradeTier();
    const { isDark: isKidsDark } = useKidsDarkMode();

    const target = nextMilestone(announced);
    const isMilestone = STREAK_MILESTONES.includes(announced);
    const bestStreak = Math.max(longestStreak, announced);
    const toGo = Math.max(0, target - announced);
    const progressPct = target > 0 ? Math.min(100, (announced / target) * 100) : 100;

    // Experience-System palette for the overlay, theme-aware. Warm ink + a single
    // ember accent — the celebration reads as *Novari*, not a generic game badge.
    // Follows the active palette (Paper, Sky, Focus) in light and dark.
    const c = {
        scrim: "color-mix(in srgb, var(--bg-primary) 95%, transparent)",
        ink: "var(--text-primary)", ink2: "var(--text-secondary)", muted: "var(--text-muted)",
        ember: "var(--accent-primary)", onEmber: "var(--accent-on-primary)",
        border: "var(--border-default)",
        track: "color-mix(in srgb, var(--text-primary) 10%, transparent)",
        glow: "color-mix(in srgb, var(--accent-primary) 22%, transparent)",
        spark1: "var(--accent-primary)", spark2: "var(--accent-warning)",
        chip: "color-mix(in srgb, var(--text-primary) 5%, transparent)",
    };

    // Same gap as LevelUpOverlay.tsx: this whole component was the muted
    // editorial "ember" celebration with zero kids-mode awareness. Reuses
    // all the trigger/acknowledgment logic above; only the rendered JSX
    // differs for a kids account. Found 2026-09-08 reviewing Kids Mode
    // feedback.
    if (isKids) {
        return (
            <AnimatePresence onExitComplete={onExitComplete}>
                {visible && (
                    <motion.div
                        key="kids-streak-overlay"
                        role="dialog"
                        aria-modal="true"
                        aria-label={`${announced}-day study streak`}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0, transition: { duration: 0.4 } }}
                    className="fixed inset-0 z-celebration flex items-center justify-center overflow-hidden px-6"
                        style={{ background: isKidsDark ? "rgba(19,31,46,0.94)" : "rgba(255,248,225,0.96)" }}
                        onClick={() => setShow(false)}
                    >
                        <div className="absolute inset-0 pointer-events-none">
                            {[...Array(14)].map((_, i) => (
                                <motion.div
                                    key={i}
                                    className="absolute rounded-full"
                                    style={{
                                        width: Math.random() * 8 + 4,
                                        height: Math.random() * 8 + 4,
                                        left: `${Math.random() * 100}%`,
                                        bottom: "-4%",
                                        background: ["#FFC800", "#FF9A3C", "#58CC02", "#1CB0F6", "#CE82FF"][i % 5],
                                    }}
                                    animate={{ y: ["0%", "-110vh"], opacity: [0, 1, 0], rotate: 360 }}
                                    transition={{ duration: Math.random() * 2 + 3, repeat: Infinity, delay: Math.random() * 2.5, ease: "easeOut" }}
                                />
                            ))}
                        </div>

                        <div
                            className="relative z-10 w-full max-w-sm flex flex-col items-center text-center rounded-[36px] p-8 shadow-2xl border-4"
                            style={{
                                background: isKidsDark ? KT.card(true) : "#ffffff",
                                borderColor: "#FF9A3C",
                                borderBottomWidth: 8,
                                borderBottomColor: "#D97706",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <motion.div
                                initial={{ scale: 0.6, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ type: "spring", stiffness: 220, damping: 14 }}
                                className="w-24 h-24 rounded-full flex items-center justify-center shadow-lg"
                                style={{ background: "linear-gradient(160deg,#FF9A3C,#FF6B35)", border: "4px solid white" }}
                            >
                                <Flame className="w-11 h-11 text-white fill-white" />
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                                className="mt-4 text-6xl font-black"
                                style={{ color: "#FF6B35" }}
                            >
                                {announced}
                            </motion.div>
                            <p className="text-sm font-semibold -mt-1" style={{ color: KT.sub(isKidsDark) }}>
                                {announced === 1 ? "day streak!" : "days streak!"}
                            </p>

                            <motion.h1
                                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                                className="text-xl font-semibold mt-4"
                                style={{ color: KT.heading(isKidsDark) }}
                            >
                                {isMilestone ? "Wow, a milestone! 🏆" : "You came back! 🎉"}
                            </motion.h1>

                            {toGo > 0 && (
                                <motion.div
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
                                    className="mt-5 w-full"
                                >
                                    <div className="flex items-center justify-between text-[12px] font-semibold mb-1.5" style={{ color: KT.sub(isKidsDark) }}>
                                        <span>Next: {target} days</span>
                                        <span>{toGo} to go</span>
                                    </div>
                                    <div className="h-3 w-full rounded-full overflow-hidden border-2" style={{ background: isKidsDark ? "rgba(255,255,255,0.08)" : "#F1F5F9", borderColor: isKidsDark ? "rgba(255,255,255,0.1)" : "#E5E7EB" }}>
                                        <motion.div
                                            initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
                                            transition={{ duration: 1, delay: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
                                            className="h-full rounded-full"
                                            style={{ background: "linear-gradient(90deg,#FFC800,#FF9A3C)" }}
                                        />
                                    </div>
                                </motion.div>
                            )}

                            <motion.button
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
                                onClick={() => setShow(false)}
                                className="mt-7 h-12 px-8 rounded-full font-black text-white text-sm active:scale-95 transition-all shadow-lg"
                                style={{ background: "#58CC02", borderBottom: "4px solid #46A302" }}
                            >
                                Keep It Up!
                            </motion.button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        );
    }

    return (
        <AnimatePresence onExitComplete={onExitComplete}>
            {visible && (
                <motion.div
                    key="streak-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label={`${announced}-day study streak`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.5, ease: "easeInOut" } }}
                    className="fixed inset-0 z-celebration flex items-center justify-center overflow-hidden backdrop-blur-xl px-6"
                    style={{ background: c.scrim }}
                    onClick={() => setShow(false)}
                >
                    {/* Soft ember glow behind the number */}
                    <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(circle at 50% 42%, ${c.glow}, transparent 60%)` }} />

                    {/* Rising embers — ember/amber only, sparse and slow */}
                    <div className="absolute inset-0 pointer-events-none">
                        {[...Array(12)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="absolute rounded-full"
                                style={{
                                    width: Math.random() * 5 + 2,
                                    height: Math.random() * 5 + 2,
                                    left: `${Math.random() * 100}%`,
                                    bottom: "-4%",
                                    background: i % 2 ? c.spark1 : c.spark2,
                                }}
                                animate={{ y: ["0%", "-110vh"], opacity: [0, 0.85, 0] }}
                                transition={{ duration: Math.random() * 2 + 3.5, repeat: Infinity, delay: Math.random() * 3, ease: "easeOut" }}
                            />
                        ))}
                    </div>

                    <div
                        className="relative z-10 w-full max-w-sm flex flex-col items-center text-center"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <motion.p
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.05 }}
                            className="text-label inline-flex items-center gap-2"
                            style={{ color: c.ember }}
                        >
                            <Flame className="w-4 h-4" /> {isMilestone ? "Milestone reached" : "Your streak"}
                        </motion.p>

                        {/* The number is the hero — set large in Fraunces (the coach's voice) */}
                        <motion.div
                            initial={{ scale: 0.7, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            transition={{ type: "spring", stiffness: 190, damping: 16, delay: 0.1 }}
                            className="relative mt-2"
                        >
                            <span
                                className="font-serif leading-none tabular-nums"
                                style={{ fontSize: "clamp(84px, 26vw, 128px)", color: c.ember, textShadow: `0 8px 40px ${c.glow}` }}
                            >
                                {announced}
                            </span>
                        </motion.div>
                        {/* Week day indicators — show last 7 days with checked/unchecked */}
                        <motion.div
                            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.20 }}
                            className="mt-3 flex items-center justify-center gap-1.5"
                        >
                            {lastSevenDays().map(({ key, initial, name }, idx) => {
                                // The last 7 days ending today; a streak of N covers the last N.
                                const daysAgo = 6 - idx;
                                const isCompleted = announced > daysAgo;
                                return (
                                    <div key={key} className="flex flex-col items-center gap-0.5">
                                        <div
                                            className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                                            style={{
                                                background: isCompleted ? c.ember : c.track,
                                                color: isCompleted ? c.onEmber : c.muted,
                                            }}
                                            aria-label={`${name}${isCompleted ? ", studied" : ""}`}
                                        >
                                            {isCompleted ? "✓" : ""}
                                        </div>
                                        <span className="text-caption font-semibold" style={{ color: daysAgo === 0 ? c.ink : c.muted }} aria-hidden="true">
                                            {initial}
                                        </span>
                                    </div>
                                );
                            })}
                        </motion.div>

                        <motion.p
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
                            className="text-sm mt-2" style={{ color: c.ink2 }}
                        >
                            {announced === 1 ? "day" : "days"} in a row
                        </motion.p>

                        <motion.h1
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                            className="font-serif text-title mt-6" style={{ color: c.ink }}
                        >
                            {isMilestone ? "That's a milestone." : "You showed up again."}
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}
                            className="mt-2 text-body leading-relaxed max-w-[32ch]" style={{ color: c.ink2 }}
                        >
                            Consistency is how mastery is built. A few minutes today keeps it alive.
                        </motion.p>

                        {/* Quiet stats — hairline-divided, not boxed badges */}
                        <motion.div
                            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
                            className="mt-7 flex items-center gap-7"
                        >
                            <div className="flex flex-col items-center">
                                <span className="font-serif text-xl tabular-nums" style={{ color: c.ink }}>{bestStreak}</span>
                                <span className="text-caption font-semibold mt-0.5" style={{ color: c.muted }}>Best</span>
                            </div>
                            <div className="w-px h-9" style={{ background: c.border }} />
                            <div className="flex flex-col items-center">
                                <span className="font-serif text-xl tabular-nums" style={{ color: c.ink }}>{announced}</span>
                                <span className="text-caption font-semibold mt-0.5" style={{ color: c.muted }}>Current</span>
                            </div>
                        </motion.div>

                        {toGo > 0 && (
                            <motion.div
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
                                className="mt-7 w-full max-w-xs"
                            >
                                <div className="flex items-center justify-between text-caption mb-1.5" style={{ color: c.muted }}>
                                    <span>Next milestone · {target} days</span>
                                    <span className="tabular-nums">{toGo} to go</span>
                                </div>
                                <div className="h-1.5 w-full rounded-full overflow-hidden" style={{ background: c.track }}>
                                    <motion.div
                                        initial={{ width: 0 }} animate={{ width: `${progressPct}%` }}
                                        transition={{ duration: 1, delay: 0.75, ease: [0.2, 0.8, 0.2, 1] }}
                                        className="h-full rounded-full" style={{ background: c.ember }}
                                    />
                                </div>
                            </motion.div>
                        )}

                        <motion.button
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.85 }}
                            onClick={() => setShow(false)}
                            autoFocus
                            className="mt-8 h-12 px-8 rounded-xl font-semibold text-body-sm transition-transform hover:-translate-y-[1px] active:translate-y-0"
                            style={{ background: c.ember, color: c.onEmber, boxShadow: `0 10px 24px -10px ${c.glow}` }}
                        >
                            Keep it going
                        </motion.button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
